import { Component, OnInit, OnChanges, OnDestroy, Input, Output, EventEmitter, ElementRef, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { PDFDocumentProxy, PdfViewerComponent } from 'ng2-pdf-viewer';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'mmp5-mm-pdf-viewer',
  templateUrl: './mm-pdf-viewer.component.html',
  styleUrls: ['./mm-pdf-viewer.component.scss']
})
export class MMPdfViewerComponent implements OnInit, OnChanges, OnDestroy {

  pdf: PDFDocumentProxy = null;
  printPdfUrl = null;
  currentScale = 1.5;
  isPrinting = false;
  zoomField: UntypedFormControl = new UntypedFormControl(1);
  search = new FormControl(null);
  zoomPattern = new RegExp('-?[0-9]*(\.[0-9]+)?');

  @Input() url = null;
  @Input('show-footer') showFooter: boolean
  @Input('can-download') canDownload: boolean = true;
  @Input('block-copy') blockCopy = 1
  @Input('file-name') fileName = 'file.pdf'
  @Output() reachedEnd = new EventEmitter<void>();

  @ViewChild(PdfViewerComponent) private pdfComponent: PdfViewerComponent;

  currentPage = 1;
  toolbarHidden = false;

  pdfQuery = '';
  private didEmitEnd = false;
  private scrollEl: Element | null = null;
  private onPdfScroll = () => this.emitIfScrolledToEnd();
  private hideToolbarTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly hideToolbarDelayMs = 2000;

  constructor(private host: ElementRef<HTMLElement>) { }

  ngOnInit() {

    this.search.valueChanges.pipe(debounceTime(500)).subscribe(res => {
      this.pdfQuery = res;
      this.pdfComponent.eventBus.dispatch('find', {
        query: this.pdfQuery, type: 'again', caseSensitive: false, findPrevious: undefined, highlightAll: true, phraseSearch: true
      });
    })
  }


  applyZoom(param) {
    console.log(this.search.value)
    let val = this.zoomField.value
    if (param == 'more') {
      val = (val + 0.1).toFixed(1);
    } else {
      val = (val - 0.1).toFixed(1);
    }

    this.zoomField.setValue(parseFloat(val))
  }

  callBackFn(event: PDFDocumentProxy) {
    this.pdf = event;
    this.isPrinting = true;
    this.toolbarHidden = false;
    this.scheduleHideToolbar();
    this.emitIfReachedEnd();
    this.attachScrollWatch();
  }

  revealToolbar() {
    this.clearHideToolbarTimer();
    this.toolbarHidden = false;
  }

  scheduleHideToolbar() {
    this.clearHideToolbarTimer();
    this.hideToolbarTimer = setTimeout(() => {
      this.toolbarHidden = true;
      this.hideToolbarTimer = null;
    }, this.hideToolbarDelayMs);
  }

  onToolbarFocusOut(event: FocusEvent) {
    const next = event.relatedTarget as Node | null;
    const hotspot = event.currentTarget as HTMLElement;
    if (next && hotspot.contains(next)) {
      return;
    }
    this.scheduleHideToolbar();
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.emitIfReachedEnd();
  }

  ngOnChanges() {
    this.currentPage = 1;
    this.didEmitEnd = false;
    this.toolbarHidden = false;
    this.clearHideToolbarTimer();
    this.detachScrollWatch();
  }

  ngOnDestroy() {
    this.clearHideToolbarTimer();
    this.detachScrollWatch();
  }

  navigateTo(step) {
    this.currentPage += step;
    this.emitIfReachedEnd();
  }

  private emitIfReachedEnd() {
    const pages = this.pdf?._pdfInfo?.numPages;
    if (!pages || this.didEmitEnd) {
      return;
    }
    if (this.currentPage >= pages) {
      this.didEmitEnd = true;
      this.reachedEnd.emit();
    }
  }

  private attachScrollWatch() {
    this.detachScrollWatch();
    const root = this.host.nativeElement;
    this.scrollEl =
      root.querySelector('.ng2-pdf-viewer-container') ||
      root.querySelector('pdf-viewer');
    this.scrollEl?.addEventListener('scroll', this.onPdfScroll, { passive: true });
    setTimeout(() => this.emitIfScrolledToEnd(), 0);
  }

  private detachScrollWatch() {
    this.scrollEl?.removeEventListener('scroll', this.onPdfScroll);
    this.scrollEl = null;
  }

  private emitIfScrolledToEnd() {
    if (this.didEmitEnd || !this.scrollEl) {
      return;
    }
    const el = this.scrollEl as HTMLElement;
    if (el.scrollHeight <= el.clientHeight + 8) {
      if (this.pdf && !this.didEmitEnd) {
        this.didEmitEnd = true;
        this.reachedEnd.emit();
      }
      return;
    }
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      this.didEmitEnd = true;
      this.reachedEnd.emit();
    }
  }

  print() {
    this.pdf.getData().then((u8) => {
      let blob = new Blob([u8.buffer], {
        type: 'application/pdf'
      });

      const blobUrl = window.URL.createObjectURL((blob));

      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = blobUrl;
      a.download = this.fileName || 'file.pdf';
      document.body.appendChild(a);
      a.click();
    });
  }

  private clearHideToolbarTimer() {
    if (this.hideToolbarTimer) {
      clearTimeout(this.hideToolbarTimer);
      this.hideToolbarTimer = null;
    }
  }
}
